import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../api";
import type { ProviderResetCredits, ProviderUsageInfo } from "../types";
import { completeResetAttempt, prepareResetAttempt, readPendingResetAttempt, savePendingResetAttempt, type PendingResetAttempt } from "../reset-credits";
import { publishProviderUsage } from "../usage";

export function ResetCreditsPanel({
  providerID,
  onUsageChange,
  onBusyChange,
}: {
  providerID: string;
  onUsageChange: (data: ProviderUsageInfo | null) => void;
  onBusyChange?: (busy: boolean) => void;
}) {
  const [details, setDetails] = useState<ProviderResetCredits | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingResetAttempt | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [refreshWarning, setRefreshWarning] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const busyRef = useRef(false);

  async function loadCredits(preserveError = false) {
    const seq = ++requestSeq.current;
    setLoading(true);
    if (!preserveError) setDetails(null);
    if (!preserveError) setError(null);
    try {
      const data = await api.providerResetCredits(providerID);
      const attempt = data.pendingAttempt
        ? savePendingResetAttempt(providerID, { ...data.pendingAttempt, accountKey: data.accountKey })
        : readPendingResetAttempt(providerID, data.accountKey);
      if (seq !== requestSeq.current) return;
      setDetails(data);
      setPending(attempt);
    } catch (e) {
      if (seq !== requestSeq.current) return;
      if (e instanceof ApiError && [401, 403, 404].includes(e.status)) setDetails(null);
      if (!preserveError) setError((e as Error).message);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }

  useEffect(() => {
    if (providerID === "openai") void loadCredits();
    return () => { requestSeq.current += 1; };
  }, [providerID]);

  useEffect(() => { onBusyChange?.(busy || loading); }, [busy, loading, onBusyChange]);
  useEffect(() => () => { onBusyChange?.(false); }, [onBusyChange]);

  async function refreshUsageOnly() {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    try {
      const data = await api.providerUsage(providerID);
      publishProviderUsage(providerID, data);
      onUsageChange(data);
      setRefreshWarning(null);
    } catch (e) {
      setRefreshWarning((e as Error).message);
    } finally {
      await loadCredits();
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function consumeReset() {
    if (!details || loading || busyRef.current || (!pending && details.availableCount <= 0)) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    setNotice(null);
    setRefreshWarning(null);
    let attempt: PendingResetAttempt | null = null;
    try {
      attempt = prepareResetAttempt(providerID, details.accountKey);
      setPending(attempt);
      const result = await api.consumeProviderResetCredit(providerID, attempt);
      if (result.accountKey !== attempt.accountKey) {
        throw new Error("A conta conectada mudou. Consulte as redefinições novamente antes de continuar.");
      }
      const messages = {
        reset: "Uso redefinido pela OpenAI. Uma redefinição foi consumida nesta conta.",
        alreadyRedeemed: "Esta tentativa já foi concluída. Nenhuma nova redefinição foi consumida ao repetir a consulta.",
        nothingToReset: "A OpenAI informou que não há uso para redefinir. Nenhuma redefinição foi aplicada.",
        noCredit: "A OpenAI informou que esta conta não tem redefinições disponíveis. Nenhuma redefinição foi aplicada.",
      };
      if (!Object.hasOwn(messages, result.outcome)) {
        throw new Error("A OpenAI não confirmou um resultado válido. Tente consultar novamente a mesma redefinição.");
      }
      completeResetAttempt(providerID, attempt);
      setPending(null);
      setNotice(messages[result.outcome]);
      publishProviderUsage(providerID, result.usage);
      onUsageChange(result.usage ?? null);
      if (!result.usage) {
        setRefreshWarning(result.usageRefreshError ?? "O resultado foi confirmado, mas não foi possível atualizar os limites. Consulte o uso novamente.");
      }
      await loadCredits();
    } catch (e) {
      // Uma resposta perdida pode esconder um resgate concluído. A próxima ação
      // retoma o mesmo UUID salvo; fechar/recarregar a tela não cria outro resgate.
      setError((e as Error).message);
      if (attempt) setPending(attempt);
      if (attempt && e instanceof ApiError && e.status === 409 && e.detail && typeof e.detail === "object") {
        const shared = (e.detail as { pendingAttempt?: unknown }).pendingAttempt;
        if (shared && typeof shared === "object") {
          try {
            const recovered = savePendingResetAttempt(providerID, { ...shared, accountKey: attempt.accountKey } as PendingResetAttempt);
            setPending(recovered);
          } catch {
            // Uma pendência inválida não substitui a UUID já persistida.
          }
        }
      }
      // Uma outra aba pode já ter enviado um UUID diferente. A consulta recupera
      // a tentativa da conta sem iniciar nem repetir o resgate automaticamente.
      await loadCredits(true);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  if (providerID !== "openai") return null;
  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
      <strong>Redefinições de uso da conta</strong>
      <p style={{ color: "var(--text-muted)", margin: "6px 0" }}>
        O resgate consome uma redefinição disponível e se aplica à conta OpenAI conectada,
        incluindo o uso no Codex e em outras sessões.
      </p>
      {loading ? <p role="status">Consultando redefinições disponíveis…</p> : null}
      {details ? <p>Redefinições disponíveis: <strong>{details.availableCount}</strong></p> : null}
      {notice ? <p role="status">{notice}</p> : null}
      {error ? <p role="alert" style={{ color: "var(--danger, #c33)" }}>{error}</p> : null}
      {refreshWarning ? <p role="status" style={{ color: "var(--text-muted)" }}>{refreshWarning}</p> : null}
      {pending ? (
        <p style={{ color: "var(--text-muted)" }}>
          Há uma tentativa sem resultado confirmado nesta aba. Tentar novamente retoma a mesma
          redefinição, mesmo após fechar ou recarregar esta tela.
        </p>
      ) : null}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {details && (details.availableCount > 0 || pending) ? (
          <button className="btn sm primary" disabled={busy || loading} onClick={() => void consumeReset()}>
            {busy ? "Aguarde…" : pending ? "Tentar novamente a mesma redefinição" : "Redefinir uso — consumir 1 redefinição"}
          </button>
        ) : null}
        <button className="btn sm" disabled={busy || loading} onClick={() => void refreshUsageOnly()}>
          Atualizar uso e redefinições
        </button>
      </div>
    </div>
  );
}
